// src/api/apiFootball.js
const API_BASE = `https://v3.football.api-sports.io`;

// ---- knobs you can tune
let RATE_LIMIT_MS = Number(250); // gap between requests
let CACHE_TTL_MS = Number(15_000); // 15s
let MAX_RETRIES = Number(3);

export function configureApi({ rateLimitMs, cacheTtlMs, maxRetries } = {}) {
    if (rateLimitMs != null) RATE_LIMIT_MS = rateLimitMs;
    if (cacheTtlMs != null) CACHE_TTL_MS = cacheTtlMs;
    if (maxRetries != null) MAX_RETRIES = maxRetries;
}

const sleep = (ms) => new Promise(r => setTimeout(r, ms));

const cache = new Map();     // key -> { data, expires }
const inflight = new Map();  // key -> Promise
let lastRequestAt = 0;       // naive rate limiter

function buildKey(path, params) {
    const qs = new URLSearchParams(params || {});
    return `${path}?${qs.toString()}`;
}

async function waitForRateSlot() {
    const now = Date.now();
    const wait = Math.max(0, lastRequestAt + RATE_LIMIT_MS - now);
    if (wait) await sleep(wait);
    lastRequestAt = Date.now();
}

async function doFetch(url, headers) {
    let attempt = 0;
    while (true) {
        const res = await fetch(url, { headers });

        if (res.status === 429 && attempt < MAX_RETRIES) {
            const ra = res.headers.get('retry-after');
            const wait = ra ? Number(ra) * 1000 : Math.min(1000 * 2 ** attempt, 8000);
            await sleep(wait);
            attempt++;
            continue;
        }

        if (!res.ok) {
            const txt = await res.text().catch(() => '');
            throw new Error(`HTTP ${res.status}${txt ? `: ${txt}` : ''}`);
        }
        const json = await res.json();
        return json?.response ?? [];
    }
}

export async function apiFetch(path, { key, params = {} } = {}) {
    const qs = new URLSearchParams(params).toString();
    const url = `${API_BASE}${path}${qs ? `?${qs}` : ''}`;
    const k = buildKey(path, params);

    // serve from cache
    const c = cache.get(k);
    if (c && c.expires > Date.now()) return c.data;

    // coalesce duplicates
    if (inflight.has(k)) return inflight.get(k);

    const headers = {
        'X-APISports-Key': key || process.env.REACT_APP_RAPIDAPI_KEY || '',
    };

    const p = (async () => {
        await waitForRateSlot();
        const data = await doFetch(url, headers);
        cache.set(k, { data, expires: Date.now() + CACHE_TTL_MS });
        return data;
    })();

    inflight.set(k, p);
    try {
        const out = await p;
        return out;
    } finally {
        inflight.delete(k);
    }
}


// Endpoints
export const getLeagues = (opts) => apiFetch('/leagues', opts); // {current:true} or by id/country
export const getFixtures = (opts) => apiFetch('/fixtures', opts); // {date, league, season, live: 'all'}
export const getStandings = (opts) => apiFetch('/standings', opts); // {league, season}
export const getTransfersByLeague = (opts) => apiFetch('/transfers', opts); // league filter via team loop (below)
export const getTeams = (opts) => apiFetch('/teams', opts); // use to map teams of a league
export const getTopScorers = (opts) => apiFetch('/players/topscorers', opts); // {league, season}

// Helpful constants
export const CDN_LEAGUE_LOGO = (id) => `https://media.api-sports.io/football/leagues/${id}.png`;
export const CDN_TEAM_LOGO = (id) => `https://media.api-sports.io/football/teams/${id}.png`;
